import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { JobInformation } from '@planone/bau365/util-user-data';
import { combineLatest, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import * as DataAccessUserActions from './data-access-user.actions';
import { sendBusinessCardButtonActions } from './data-access-user.actions';
import { ProfileInformation, Synonym } from './data-access-user.models';
import * as DataAccessUserSelectors from './data-access-user.selectors';

@Injectable()
export class DataAccessUserFacade {
  availableProfessions$ = this.store.pipe(select(DataAccessUserSelectors.getAvailableProfessions));
  availableSynonyms$ = this.store.pipe(select(DataAccessUserSelectors.getAvailableSynonyms));
  getUserProfile$ = this.store.pipe(select(DataAccessUserSelectors.getUserProfile));
  getUserJobInformation$ = this.store.pipe(select(DataAccessUserSelectors.getUserJobInformation));
  getUserProfileImage$ = this.store.pipe(select(DataAccessUserSelectors.getUserProfileImage));
  getEmployeeCountOptions$ = this.store.pipe(select(DataAccessUserSelectors.getEmployeeCountOptions));
  getAreaOfInterestOptions$ = this.store.pipe(select(DataAccessUserSelectors.getAreaOfInterestOptions));
  profileImageUploadFinished$ = this.store.pipe(select(DataAccessUserSelectors.isProfileImageUploaded));
  profileImageUploadError$ = this.store.pipe(select(DataAccessUserSelectors.isProfileImageUploadError));
  profileCompleted$ = this.store.pipe(
    select(DataAccessUserSelectors.getProfileLoadingFailed),
    filter((failed) => failed !== undefined),
    map((failed) => !failed)
  );
  profileCompletionRedirectUrl$ = this.store.pipe(select(DataAccessUserSelectors.getUserProfileCompletionRedirectUrl));
  profileCompletionMode$ = this.store.pipe(select(DataAccessUserSelectors.getUserProfileCompletionMode));
  profession$ = this.store.pipe(select(DataAccessUserSelectors.getUserProfession));
  specialization$ = this.store.pipe(select(DataAccessUserSelectors.getUserSpecialization));
  synonym$ = this.store.pipe(select(DataAccessUserSelectors.getUserSynonym));
  personalisationInfo$ = this.store.pipe(select(DataAccessUserSelectors.getUserPersonalizationInfo));
  deletionFormSubmitted$ = this.store.pipe(select(DataAccessUserSelectors.getProfileDeletionSubmitted));
  businessCardLoaded$ = this.store.pipe(select(DataAccessUserSelectors.isBusinessCardLoaded));
  businessCardId$ = this.store.pipe(select(DataAccessUserSelectors.getBusinessCardId));
  businessCardRequiredFields$ = this.store.pipe(select(DataAccessUserSelectors.getBusinessCardMandatoryFields));
  businessCardCreationReady$ = combineLatest([
    this.store.pipe(select(DataAccessUserSelectors.getUserJobInformation)),
    this.store.pipe(select(DataAccessUserSelectors.getBusinessCardMandatoryFields)),
  ]).pipe(
    map(([jobInformation, mandatoryFields]) => {
      if (typeof jobInformation === 'undefined') return false;
      return mandatoryFields.every(
        (field) =>
          Object.entries(jobInformation).find(([key, value]) => field === key && value !== undefined) !== undefined
      );
    })
  );
  businessCardReleasedFields$ = combineLatest([
    this.store.pipe(select(DataAccessUserSelectors.getUserJobInformation)),
    this.store.pipe(select(DataAccessUserSelectors.getBusinessCardFields)),
  ]).pipe(
    switchMap(([jobInformation, fields]) =>
      of(
        Object.entries(jobInformation ?? {})
          .filter(([_, value]) => typeof value !== 'undefined' && value !== '')
          .map(([key]) => key)
      ).pipe(
        map((filledFields) => fields.filter((field) => filledFields.includes(field.name))),
        map((fields) => fields.map(({ label }) => label))
      )
    )
  );
  sendBusinessCardSuccess = (exhibitorId: string) =>
    this.store.pipe(select(DataAccessUserSelectors.getSendBusinessCardSuccess(exhibitorId)));

  constructor(private readonly store: Store) {}

  pageOpened() {
    this.store.dispatch(DataAccessUserActions.userProfileCompletionPageOpened());
  }

  professionSpecializationSubmitted(profession: string, specialization: string, synonym?: Synonym) {
    this.store.dispatch(
      DataAccessUserActions.professionSpecializationSubmitted({ profession, specialization, synonym })
    );
  }

  userProfilePageOpened() {
    this.store.dispatch(DataAccessUserActions.userProfilePageOpened());
  }

  userBusinessCardPageOpened() {
    this.store.dispatch(DataAccessUserActions.userBusinessCardPageOpened());
  }

  profileSubmitted(profileInformation: ProfileInformation) {
    this.store.dispatch(DataAccessUserActions.profileSubmitted(profileInformation));
  }

  jobInformationSubmitted(jobInformation: JobInformation) {
    this.store.dispatch(DataAccessUserActions.userJobInformationSubmitted({ jobInformation }));
  }

  newsletterSubscriptionSubmitted(email: string) {
    this.store.dispatch(DataAccessUserActions.newsletterSubscriptionSubmitted({ email: email }));
  }

  deleteAccountSubmitted(reasonValues: { predefinedReason: string; otherReason: string }) {
    this.store.dispatch(DataAccessUserActions.deleteAccountSubmitted(reasonValues));
  }

  showBusinessCard() {
    this.store.dispatch(DataAccessUserActions.showBusinessCard());
  }

  startBusinessCardCreation() {
    this.store.dispatch(DataAccessUserActions.startBusinessCardCreation());
  }

  changeBusinessCardDetails() {
    this.store.dispatch(DataAccessUserActions.changeBusinessCardDetails());
  }

  createBusinessCard() {
    this.store.dispatch(DataAccessUserActions.createBusinessCard());
  }

  resetBusinessCard() {
    this.store.dispatch(DataAccessUserActions.resetBusinessCard());
  }

  downloadQrCode(canvas: HTMLCanvasElement) {
    this.store.dispatch(DataAccessUserActions.downloadBusinessCardQrCode({ canvas }));
  }

  profileImageSubmitted(profileImage: File) {
    this.store.dispatch(DataAccessUserActions.userProfileImageSubmitted({ profileImage }));
  }

  deleteProfileImage() {
    this.store.dispatch(DataAccessUserActions.userProfileImageDelete());
  }

  initSendBusinessCardButton() {
    this.store.dispatch(sendBusinessCardButtonActions.initButton());
  }

  sendBusinessCard(fairId: string, exhibitorId: string): void {
    this.store.dispatch(sendBusinessCardButtonActions.sendBusinessCardRequested({ fairId, exhibitorId }));
  }
}
